import { AdditionalLink } from "./AdditionalLink";
import { Box, Details, Heading } from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewJobDataContext } from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import {
  surfaceForAdditionalLinksTestID,
  useSeoLinkGraphSink,
} from "../../../../seo/SeoLinkGraphCollector.web";

import { colors } from "@indeed/ifl-tokens";
import React from "react";
import { StyleSheet } from "react-native";

const CONTENT_MARGIN = 16;

interface DetailsSectionProps {
  links: viewjob.model.AdditionalLinksElementModel[];
  headingLabel: string;
  includeSuffix: boolean;
  includeFromParam?: boolean;
  testID?: string;
  isLast?: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({
  links,
  headingLabel,
  includeSuffix,
  includeFromParam,
  testID,
  isLast = false,
}) => {
  if (links.length === 0) return null;

  const fromParameter = includeFromParam ? "viewjob" : undefined;

  return (
    <Box sx={!isLast ? styles.sectionWithBorder : undefined}>
      <Details
        heading={
          <Heading level={3} sx={styles.headingText}>
            {headingLabel}
          </Heading>
        }
        defaultOpen={false}
        sx={{ paddingHorizontal: CONTENT_MARGIN }}
      >
        <Box id={testID} sx={styles.linksContainer}>
          {links.map((link) => (
            <AdditionalLink
              key={link.surfaceElementUrl}
              link={link}
              includeSuffix={includeSuffix}
              fromParameter={fromParameter}
            />
          ))}
        </Box>
      </Details>
    </Box>
  );
};

export const AdditionalLinks: React.FC = () => {
  const { pgettext } = useI18n();
  const { initialData, viewjobSourceUrl, viewjobOneGraphModel } = useViewJobDataContext();
  const seoLinkGraphSink = useSeoLinkGraphSink();
  const rnViewJobDisplay = useRnViewJobDisplay();
  const additionalLinksViewModel =
    viewjobOneGraphModel?.additionalLinksViewModel ?? initialData?.additionalLinksViewModel;

  if (!additionalLinksViewModel) {
    return null;
  }

  const {
    companySalaryLinks = [],
    similarJobTitleLinks = [],
    similarCategoryLinks = [],
    careerGuideArticleLinks = [],
    nearbyLocationLinks = [],
  } = additionalLinksViewModel;

  // Build sections array to track which is first
  const sections = [
    {
      links: companySalaryLinks,
      testID: "company-and-salary-information",
      headingLabel: pgettext(
        "AdditionalLinks: company and salary information label",
        "Company and salary information",
      ),
      includeSuffix: false,
      includeFromParam: false,
    },
    {
      links: similarJobTitleLinks,
      testID: "similar-jobs",
      headingLabel: pgettext("AdditionalLinks: similar title label", "Jobs with similar titles"),
      includeSuffix: true,
      includeFromParam: false,
    },
    {
      links: similarCategoryLinks,
      testID: "similar-categories",
      headingLabel: pgettext("AdditionalLinks: similar category label", "Similar job categories"),
      includeSuffix: true,
      includeFromParam: false,
    },
    {
      links: careerGuideArticleLinks,
      testID: "career-guide-articles",
      headingLabel: pgettext(
        "AdditionalLinks: career guide article label",
        "Career Guide articles",
      ),
      includeSuffix: false,
      includeFromParam: true,
    },
    {
      links: nearbyLocationLinks,
      testID: "similar-jobs-nearby",
      headingLabel: pgettext("AdditionalLinks: similar jobs nearby label", "Similar jobs nearby"),
      includeSuffix: false,
      includeFromParam: false,
    },
  ].filter((section) => section.links.length > 0);

  if (sections.length === 0) {
    return null;
  }

  // Push one seoLinkGraph entry per rendered link. Entries are
  // deduplicated by the collector by (surface, index, canonicalurl).
  const viewjobJk = viewjobOneGraphModel?.job.jobKey ?? initialData?.jobKey ?? "";
  const vjtk = viewjobOneGraphModel?.mobvjtk ?? "";
  sections.forEach((section) => {
    const surface = surfaceForAdditionalLinksTestID(section.testID);
    if (!surface) return;
    section.links.forEach((link, linkIndex) => {
      seoLinkGraphSink.push({
        canonicalurl: link.surfaceElementUrl,
        pagecanonicalurl: viewjobSourceUrl,
        surface,
        index: linkIndex,
        rnViewJobDisplay,
        viewjob_jk: viewjobJk,
        vjtk,
      });
    });
  });

  return (
    <Box sx={styles.container} id="additionalLinks">
      {sections.map((section, index) => (
        <DetailsSection
          key={section.testID}
          links={section.links}
          testID={section.testID}
          headingLabel={section.headingLabel}
          includeSuffix={section.includeSuffix}
          includeFromParam={section.includeFromParam}
          isLast={index === sections.length - 1}
        />
      ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  sectionWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.neutral["200"],
  },
  headingText: {
    marginBottom: 0,
  },
  linksContainer: {
    paddingTop: 4,
  },
});

export default AdditionalLinks;
